import api from './api';

// Talks to /api/planning/* — the distribution network: typed sites, the lanes
// between them, and SKU volume down each lane. Version and site come from the
// x-budget-version-id / x-site-code headers (set by the api interceptor), so
// nothing here passes them explicitly.
//
// Every write requires the "All Sites" context. The server rejects a write made
// while scoped to one site with a 403 — a planner acting as one depot cannot
// decide what another depot receives.
export const planningService = {
  // { sites, siteTypes, lanes, vehicleTypes, flow, capacityUom }
  getNetwork: async () => {
    const res = await api.get('/planning/network');
    return res.data;
  },

  // payload: { origin_site_code, dest_site_code, lane_type?, lane_name?, notes? }
  // lane_type: 'NORMAL' (goods out, planned by SKU) | 'RETURNS' (returnable
  // packaging + quality rejects coming back, planned as a plain volume).
  // Vehicle, distance and return factor are rating attributes — not set here.
  addLane: async (payload) => {
    const res = await api.post('/planning/lanes', payload);
    return res.data;
  },

  // payload: { lane_id, ...patch }
  saveLane: async (payload) => {
    const res = await api.put('/planning/lanes', payload);
    return res.data;
  },

  deleteLane: async (laneId) => {
    const res = await api.delete('/planning/lanes', { data: { lane_id: laneId } });
    return res.data;
  },

  // positions: [{ site_code, map_x, map_y }] — saved against the SELECTED
  // budget version, since a scenario often changes the network's shape.
  saveSitePositions: async (positions) => {
    const res = await api.put('/planning/site-positions', { positions });
    return res.data;
  },

  // { lane, rows: [{ stock_code, production[12], inbound[12], committed[12],
  //   local_demand[12], dest_demand[12], lane[12] }], capacityUom }
  getLaneVolumes: async (laneId) => {
    const res = await api.get(`/planning/lanes/${laneId}/volumes`);
    return res.data;
  },

  // rows: [{ stock_code, quantities: [12] }] — a zero deletes the value.
  saveLaneVolumes: async (laneId, rows) => {
    const res = await api.put(`/planning/lanes/${laneId}/volumes`, { rows });
    return res.data;
  },

  // RETURNS lanes — 12-period series per category, plus optional product lines.
  // { lane, rows: [{ return_category, stock_code|null, stock_description,
  //   quantities[12] }], stockCodes, capacityUom }
  getLaneReturns: async (laneId) => {
    const res = await api.get(`/planning/lanes/${laneId}/returns`);
    return res.data;
  },

  // rows replace the lane outright — anything not sent is removed. stock_code
  // null = an unattributed load; a code attributes the return to one product.
  saveLaneReturns: async (laneId, rows) => {
    const res = await api.put(`/planning/lanes/${laneId}/returns`, { rows });
    return res.data;
  },

  // mode: 'replace' wipes first; 'fill' only writes what is currently empty.
  seedLaneVolumes: async (laneId, mode = 'replace') => {
    const res = await api.post(`/planning/lanes/${laneId}/seed`, { mode });
    return res.data;
  },

  // ---- Optimise -----------------------------------------------------------
  // PROPOSE. Reads and solves; writes NOTHING. A POST only because it carries
  // solver options. Returns { plan_hash, summary, lanes, unmet, meta }.
  // options: { maxLaneSharePct?, holdingCostPerUnit?, unpricedLanePolicy? }
  optimise: async (options = {}) => {
    const res = await api.post('/planning/optimise', options);
    return res.data;
  },

  // ACCEPT. Sends the lanes the user ticked and the plan_hash that came back
  // with the proposal; the SERVER re-solves and writes its own numbers, so
  // nothing here can push arbitrary volumes into a lane. A 409 PLAN_CHANGED
  // means the budget moved while the proposal was on screen — re-run it.
  // Each accepted lane is REPLACED outright, including being emptied.
  applyOptimisation: async (laneIds, planHash, options = {}) => {
    const res = await api.post('/planning/optimise/apply', {
      lane_ids: laneIds, plan_hash: planHash, ...options,
    });
    return res.data;
  },

  // rows accept either shape: { ..., quantities: [12] } or
  // { ..., financial_period, quantity }. Missing lanes are created by default.
  uploadVolumes: async (rows, { mode = 'overwrite', createLanes = true } = {}) => {
    const res = await api.post('/planning/volumes/upload', {
      rows, mode, create_lanes: createLanes,
    });
    return res.data;
  },
};

export default planningService;
